import { Injectable } from '@angular/core';
import { flatMap } from 'lodash-es';
import { NEVER, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ItemRevision } from 'src/app/models/domain';
import { PartDesignItemStructure } from 'src/app/models/domain/part-design-item-structure';
import { LegoRestApiDataService } from 'src/app/services';
import { handleError } from './akita-error-helper';
import { ValueState } from './value-state';

@Injectable({ providedIn: 'root' })
export class PartDesignService {
  private readonly state = new ValueState<PartDesignItemStructure | undefined>(undefined);

  public readonly partDesignStructure$ = this.state.value$;
  public readonly loading$ = this.state.loading$;
  public readonly error$ = this.state.error$;

  public readonly combinedItemRevisionDatasets$ = this.partDesignStructure$.pipe(
    map((partDesign) => {
      if (!partDesign) {
        return [];
      }

      let result: ItemRevision[] = [];

      result = partDesign.tsItem ? partDesign.tsItem.revisions : result;
      result = partDesign.csItem ? [...result, ...partDesign.csItem.revisions] : result;
      result = partDesign.apItem ? [...result, ...partDesign.apItem.revisions] : result;

      return flatMap(result, (itemRevision) => itemRevision.datasets);
    })
  );

  public constructor(private readonly legoRestApiDataService: LegoRestApiDataService) {}

  public loadById(designId: string): Observable<void> {
    this.state.setLoading(true);

    return this.legoRestApiDataService.getItemStructureById(`PD${designId}`).pipe(
      tap((pdStructure) => {
        this.state.set(pdStructure);
      }),
      catchError((error: Error) => {
        console.error('Failed to load PD structure:', error);
        this.state.set(undefined);

        handleError(this.state, error, {
          default: () => {
            this.state.setError(error.message);
          },
        });

        return NEVER;
      }),
      map(() => undefined)
    );
  }
}
